import { fetchApi } from '@/lib/api';
import { factoryInit, queryString, type FactoryRequestOptions } from '@/lib/factory-request';
import type { FlowSummary, RunDetail, RunMessageBody, RunMessageOutcome, RunQueuedMessage, RunStatus, RunThreadSummary, RunSummary, RunTurnPrompt, StartRunBody } from '@shared/types/factory';
import type { GitDiffResult, T3ActivityResult, WorktreeImageResult } from '@shared/agent-protocol';

/** Flows and runs (docs/first-slice-plan.md §3, "Flows and runs"). */

export interface RunListQuery {
  task_id?: string;
  /** One status, or several (any of them). */
  status?: RunStatus | readonly RunStatus[] | null;
  /** From 1. */
  page?: number;
  limit?: number;
}

export interface RunListPage<Row = RunSummary> {
  runs: Row[];
  total: number;
  page: number;
  limit: number;
}

/** A run in the cross-project list, with its project. */
export type RunAcrossProjects = RunSummary & { project: { id: string; slug: string; name: string } };

/** `?status=queued,running`: the API takes a comma-separated list. */
function statusParam(status: RunListQuery['status']): string | null | undefined {
  return typeof status === 'string' || !status ? status : status.join(',');
}

export function listFlows(opts: FactoryRequestOptions = {}) {
  return fetchApi<{ flows: FlowSummary[] }>('/flows', factoryInit(opts));
}

/** The current project's runs, newest first. */
export function listRuns(query: RunListQuery = {}, opts: FactoryRequestOptions = {}) {
  return fetchApi<RunListPage>(
    `/runs${queryString({ task_id: query.task_id, status: statusParam(query.status), page: query.page, limit: query.limit })}`,
    factoryInit(opts)
  );
}

/** Runs across every project the caller can see. */
export function listAllRuns(query: Omit<RunListQuery, 'task_id'> = {}, opts: FactoryRequestOptions = {}) {
  return fetchApi<RunListPage<RunAcrossProjects>>(
    `/runs-all${queryString({ status: statusParam(query.status), page: query.page, limit: query.limit })}`,
    factoryInit(opts)
  );
}

/** Maintainers. 400 RUNTIME_ABOVE_CEILING when the access level is above the machine's T3 token. */
export function startRun(body: StartRunBody, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ run: RunDetail }>('/runs', factoryInit(opts, 'POST', body));
}

export function getRun(runId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ run: RunDetail }>(`/runs/${encodeURIComponent(runId)}`, factoryInit(opts));
}

/** Maintainers. The T3 thread is left alone. 409 RUN_FINISHED when it has already finished. */
export function cancelRun(runId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ run: RunDetail }>(`/runs/${encodeURIComponent(runId)}/cancel`, factoryInit(opts, 'POST'));
}

/**
 * Maintainers (SF-T-279): close the run with every run in its T3 thread: those still going are cancelled, none is carried
 * on again, their open questions close unanswered and their tasks stop waiting on the people they asked. 409 RUN_CLOSED.
 */
export function closeRun(runId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ run: RunDetail; closed: { runs: number[]; task_ids: string[] } }>(`/runs/${encodeURIComponent(runId)}/close`, factoryInit(opts, 'POST'));
}

/**
 * Maintainers (SF-T-167): a new run on the same branch and machine as this one, whose worktree setup failed;
 * the agent sets the worktree up again. 409 NOT_A_SETUP_FAILURE, or RUN_SUPERSEDED when the task has a newer run.
 */
export function retryRunSetup(runId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ run: RunDetail }>(`/runs/${encodeURIComponent(runId)}/retry-setup`, factoryInit(opts, 'POST'));
}

/**
 * Maintainers (SF-T-209): Wrap up a live run: its agent finishes and opens or updates the PR, then the checks and
 * review follow. 409 NOT_LIVE, ALREADY_WRAPPING_UP or RUN_FINISHED.
 */
export function wrapUpRun(runId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ run: RunDetail }>(`/runs/${encodeURIComponent(runId)}/wrap-up`, factoryInit(opts, 'POST'));
}

/**
 * Members (SF-T-209): a message for the run's agent from the task's Agent tab. Steer goes into the turn that's
 * running (409 THREAD_NOT_WORKING when none is); Queue after it, or as a Follow up once the run has finished.
 */
export function sendRunMessage(runId: string, body: RunMessageBody, opts: FactoryRequestOptions = {}) {
  return fetchApi<RunMessageOutcome>(`/runs/${encodeURIComponent(runId)}/messages`, factoryInit(opts, 'POST', body));
}

/**
 * Members (SF-T-273): Continue in a new worktree: the run's thread is stuck on its missing worktree folder, so a Follow
 * up makes it again at the same path, sets it up and resends the message that failed. 409 FOLDER_NOT_GONE, RUN_ACTIVE
 * or RUN_SUPERSEDED.
 */
export function restoreRunWorktree(runId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<RunMessageOutcome>(`/runs/${encodeURIComponent(runId)}/restore-worktree`, factoryInit(opts, 'POST'));
}

/** SF-T-266: Stop: the live run's turn interrupted (the run waits for the next message). */
export function stopRunTurn(runId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ run_number: number }>(`/runs/${encodeURIComponent(runId)}/stop`, factoryInit(opts, 'POST'));
}

/** SF-T-266: the messages a live run holds for its agent's next turn. */
export function listQueuedMessages(runId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ messages: RunQueuedMessage[] }>(`/runs/${encodeURIComponent(runId)}/messages`, factoryInit(opts));
}

/** SF-T-266: a queued message's new words, before it goes. 409 MESSAGE_GONE or MESSAGE_SENDING. */
export function editQueuedMessage(runId: string, messageId: string, body: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ messages: RunQueuedMessage[] }>(`/runs/${encodeURIComponent(runId)}/messages/${encodeURIComponent(messageId)}`, factoryInit(opts, 'PATCH', { body }));
}

/** SF-T-266: a queued message taken off the queue. */
export function removeQueuedMessage(runId: string, messageId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ messages: RunQueuedMessage[] }>(`/runs/${encodeURIComponent(runId)}/messages/${encodeURIComponent(messageId)}`, factoryInit(opts, 'DELETE'));
}

/** SF-T-266: Send now: a queued message steered into the turn that's running, and off the queue. */
export function sendQueuedNow(runId: string, messageId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<RunMessageOutcome>(`/runs/${encodeURIComponent(runId)}/messages/${encodeURIComponent(messageId)}/send-now`, factoryInit(opts, 'POST'));
}

/**
 * SF-T-222: one file's changes on the run's branch, read live from its worktree on the machine (against where the
 * branch left its base, uncommitted work included). 409 AGENT_OUTDATED, MACHINE_OFFLINE or NO_WORKTREE; 404 WORKTREE_NOT_FOUND.
 */
export function getRunFileDiff(runId: string, path: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ diff: GitDiffResult }>(`/runs/${encodeURIComponent(runId)}/diff${queryString({ path })}`, factoryInit(opts));
}

/**
 * SF-T-266: a picture the run's agent put in a message (`![…](/abs/path.png)`), read live from its worktree on the
 * machine. 404 IMAGE_NOT_IN_WORKTREE or IMAGE_NOT_FOUND; 409 AGENT_OUTDATED or MACHINE_OFFLINE.
 */
export function getRunImage(runId: string, path: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ image: WorktreeImageResult }>(`/runs/${encodeURIComponent(runId)}/image${queryString({ path })}`, factoryInit(opts));
}

/** SF-T-222 (Members): a light AI call's few sentences on where the run's agent is at, not saved on the task. 503 AI_NOT_CONFIGURED, 429 TOO_MANY_SUMMARIES. */
export function summarizeRunThread(runId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ summary: RunThreadSummary }>(`/runs/${encodeURIComponent(runId)}/thread-summary`, factoryInit(opts, 'POST', {}));
}

/** SF-T-312: the text one of the run's turns sent its agent. 404 TURN_NOT_FOUND or PROMPT_NOT_STORED. */
export function getRunTurnPrompt(runId: string, turnId: string, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ prompt: RunTurnPrompt }>(`/runs/${encodeURIComponent(runId)}/turns/${encodeURIComponent(turnId)}/prompt`, factoryInit(opts));
}

/** The project's run with this number (R-12 → 12). 404 when there's none. */
export function getRunByNumber(number: number, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ run: RunDetail }>(`/runs/by-number/${encodeURIComponent(String(number))}`, factoryInit(opts));
}

export interface RunActivityQuery {
  /** One of the run thread's subagents. */
  subagent?: string | null;
  /** Items after this position (what's new), or before it (paging back). */
  after?: number | null;
  before?: number | null;
  limit?: number | null;
  /** One item's full text, from `offset`. */
  item?: string | null;
  offset?: number | null;
}

/**
 * The run's coding agent at work (T-27), read live from its machine and never stored: the thread's latest
 * items, what's new after a position, earlier ones, or one item's full text. 409 MACHINE_OFFLINE,
 * AGENT_OUTDATED or NO_THREAD; 404 THREAD_NOT_FOUND; 504 MACHINE_TIMEOUT.
 */
export function getRunActivity(runId: string, query: RunActivityQuery = {}, opts: FactoryRequestOptions = {}) {
  return fetchApi<{ activity: T3ActivityResult }>(
    `/runs/${encodeURIComponent(runId)}/activity${queryString({ ...query })}`,
    factoryInit(opts)
  );
}
